import HandymanRounded from "@mui/icons-material/HandymanRounded"
import ScheduleRounded from "@mui/icons-material/ScheduleRounded"
import { Box, Stack, Typography, useTheme } from "@mui/material"
import { keyframes } from "@mui/material/styles"
import moment from "moment"
import { useEffect, useMemo, useState } from "react"
import logoWide from "src/assets/images/Logo-2.png"
import { formatMaintenanceTime } from "./format"
import { MaintenanceSnapshot } from "./types"

const pulseRing = keyframes`
  0% { transform: scale(0.86); opacity: 0.45; }
  70% { transform: scale(1.28); opacity: 0; }
  100% { transform: scale(1.28); opacity: 0; }
`

const spinSlow = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`

function pad(n: number) {
  return String(n).padStart(2, "0")
}

function useRemaining(iso?: string | null) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!iso) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [iso])

  return useMemo(() => {
    if (!iso) return null
    const end = moment(iso)
    if (!end.isValid()) return null
    const diff = Math.max(0, end.valueOf() - now)
    const total = Math.floor(diff / 1000)
    return {
      overdue: diff <= 0,
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60
    }
  }, [iso, now])
}

function TimeCell({ value, label }: { value: string; label: string }) {
  return (
    <Box sx={{ minWidth: 56, textAlign: "center" }}>
      <Box
        sx={{
          px: 1.25,
          py: 1,
          borderRadius: 2,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: { xs: 20, sm: 24 },
          fontWeight: 700,
          letterSpacing: 1,
          lineHeight: 1,
          bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)"),
          border: "1px solid",
          borderColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)")
        }}
      >
        {value}
      </Box>
      <Typography variant="caption" sx={{ display: "block", mt: 0.6, color: "text.secondary", letterSpacing: 0.6 }}>
        {label}
      </Typography>
    </Box>
  )
}

const MaintenanceMark = () => (
  <Box
    sx={{
      position: "relative",
      width: 96,
      height: 96,
      mx: "auto",
      animation: `${float} 4.5s ease-in-out infinite`,
      "@media (prefers-reduced-motion: reduce)": { animation: "none" }
    }}
  >
    {[0, 1].map((i) => (
      <Box
        key={i}
        sx={{
          position: "absolute",
          inset: 8,
          borderRadius: "50%",
          border: "1px solid",
          borderColor: "rgba(245, 158, 11, 0.35)",
          animation: `${pulseRing} 2.8s ease-out infinite`,
          animationDelay: `${i * 0.9}s`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" }
        }}
      />
    ))}
    <Box
      sx={{
        position: "absolute",
        inset: 6,
        borderRadius: "50%",
        border: "1.5px dashed rgba(245, 158, 11, 0.55)",
        animation: `${spinSlow} 18s linear infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" }
      }}
    />
    <Box
      sx={{
        position: "absolute",
        inset: 18,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#B45309",
        bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(245, 158, 11, 0.16)" : "#FFF7ED"),
        boxShadow: "0 10px 30px rgba(180, 83, 9, 0.18)"
      }}
    >
      <HandymanRounded sx={{ fontSize: 30 }} />
    </Box>
  </Box>
)

const MaintenanceScreen = ({ status }: { status: MaintenanceSnapshot }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === "dark"
  const remaining = useRemaining(status.scheduledEndAt)

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        overflowY: "auto",
        color: "text.primary",
        backgroundColor: isDark ? "#020617" : "#F8FAFC",
        backgroundImage: isDark
          ? [
              "radial-gradient(900px 420px at 50% -8%, rgba(245, 158, 11, 0.16), transparent 60%)",
              "radial-gradient(700px 380px at 100% 100%, rgba(56, 189, 248, 0.08), transparent 55%)",
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0)"
            ].join(",")
          : [
              "radial-gradient(900px 420px at 50% -8%, rgba(255, 237, 213, 0.95), transparent 62%)",
              "radial-gradient(700px 380px at 110% 110%, rgba(226, 232, 240, 0.9), transparent 55%)",
              "radial-gradient(circle at 1px 1px, rgba(15,23,42,0.06) 1px, transparent 0)"
            ].join(","),
        backgroundSize: "auto, auto, 28px 28px"
      }}
    >
      <Box
        sx={{
          minHeight: "100%",
          display: "grid",
          placeItems: "center",
          p: { xs: 2.5, sm: 4 }
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 520,
            px: { xs: 3, sm: 5 },
            py: { xs: 2.75, sm: 3.25 },
            borderRadius: 4,
            textAlign: "center",
            bgcolor: isDark ? "rgba(15, 23, 42, 0.72)" : "rgba(255,255,255,0.9)",
            border: "1px solid",
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)",
            boxShadow: isDark ? "0 30px 80px rgba(0,0,0,0.35)" : "0 30px 80px rgba(15,23,42,0.10)",
            backdropFilter: "blur(18px)"
          }}
        >
          <Box
            component="img"
            src={logoWide}
            alt="Three Shelves"
            sx={{
              width: 132,
              mb: 1,
              filter: isDark ? "invert(1)" : "none"
            }}
          />
          <MaintenanceMark />

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mt: 1.75,
              mb: 1.5,
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 0.4,
              color: "#B45309",
              bgcolor: isDark ? "rgba(245, 158, 11, 0.12)" : "#FFFBEB",
              border: "1px solid",
              borderColor: isDark ? "rgba(245, 158, 11, 0.22)" : "#FDE68A"
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: "#F59E0B",
                boxShadow: "0 0 0 4px rgba(245, 158, 11, 0.18)"
              }}
            />
            Maintenance in progress
          </Box>

          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 26, sm: 34 },
              fontWeight: 800,
              letterSpacing: -0.6,
              lineHeight: 1.15,
              color: isDark ? "#F8FAFC" : "#0F172A"
            }}
          >
            {status.title}
          </Typography>

          <Typography
            sx={{
              mt: 1.5,
              mx: "auto",
              maxWidth: 420,
              fontSize: { xs: 15, sm: 16 },
              lineHeight: 1.6,
              color: isDark ? "rgba(226,232,240,0.78)" : "#64748B"
            }}
          >
            {status.message}
          </Typography>

          {remaining && !remaining.overdue && (
            <Stack spacing={1} alignItems="center" sx={{ mt: 2.25 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: isDark ? "#CBD5E1" : "#64748B" }}>
                <ScheduleRounded sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
                  Expected back {formatMaintenanceTime(status.scheduledEndAt)}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="center">
                {remaining.days > 0 && (
                  <>
                    <TimeCell value={pad(remaining.days)} label="Days" />
                    <Typography sx={{ mt: 1.4, fontWeight: 700, color: "text.secondary" }}>:</Typography>
                  </>
                )}
                <TimeCell value={pad(remaining.hours)} label="Hours" />
                <Typography sx={{ mt: 1.4, fontWeight: 700, color: "text.secondary" }}>:</Typography>
                <TimeCell value={pad(remaining.minutes)} label="Mins" />
                <Typography sx={{ mt: 1.4, fontWeight: 700, color: "text.secondary" }}>:</Typography>
                <TimeCell value={pad(remaining.seconds)} label="Secs" />
              </Stack>
            </Stack>
          )}

          {status.scheduledEndAt && remaining?.overdue && (
            <Typography variant="body2" sx={{ mt: 3, color: isDark ? "#CBD5E1" : "#64748B" }}>
              Wrapping up now — thanks for waiting.
            </Typography>
          )}

          {/* <Button
            size="large"
            variant="contained"
            startIcon={<RefreshRounded />}
            onClick={() => window.location.reload()}
            sx={{
              mt: 2.5,
              px: 3.5,
              py: 1.1,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 15,
              bgcolor: isDark ? "#F8FAFC" : "#0F172A",
              color: isDark ? "#0F172A" : "#FFFFFF",
              boxShadow: "none",
              "&:hover": {
                bgcolor: isDark ? "#E2E8F0" : "#1E293B",
                boxShadow: "none"
              }
            }}
          >
            Try again
          </Button> */}

          <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "text.secondary" }}>
            You can leave this tab open. It will return on its own when we are back.
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default MaintenanceScreen
